import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { lazy, useCallback, useState } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { RenameDropdownItem } from './Menu/RenameDropdownItem';
import { ProjectRenameDropdownContent } from './ProjectRenameDropdownContent.client';
import { Avatar } from '~/components/ui/Avatar';
import { Button } from '~/components/ui/Button';
import { LazyLoadWrapper, retry } from '~/components/ui/LazyLoadWrapper';
import { Skeleton } from '~/components/ui/Skeleton';
import { chatStore } from '~/lib/stores/chat';
import { descriptionStore, setDescription } from '~/lib/stores/description';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import { dropdownAnimationVariants } from '~/utils/animationVariants';
import { classNames } from '~/utils/classNames';
import { visibilityOptions } from '~/utils/projectVisibility';

type DropdownVisibility = 'none' | 'edit-title' | 'menu';

const AccountSelectorMenu = lazy(
  retry(() => import('../sidebar/AccountSelector.client').then((mod) => ({ default: mod.AccountSelectorMenu }))),
);
const DuplicateMenuItem = lazy(
  retry(() => import('./Menu/DuplicateDropdownItem.client').then((mod) => ({ default: mod.DuplicateMenuItem }))),
);
const ExportDropdownItem = lazy(
  retry(() => import('./Menu/ExportDropdownItem.client').then((mod) => ({ default: mod.ExportDropdownItem }))),
);
const VersionHistoryDropdownItem = lazy(
  retry(() =>
    import('./Menu/VersionHistoryDropdownItem.client').then((mod) => ({ default: mod.VersionHistoryDropdownItem })),
  ),
);
const VersionHistoryRoot = lazy(
  retry(() => import('~/components/chat/version-history').then((mod) => ({ default: mod.Root }))),
);
const VisibilityDropdownItem = lazy(
  retry(() => import('./Menu/VisibilityDropdownItem.client').then((mod) => ({ default: mod.VisibilityDropdownItem }))),
);

export function ProjectHeader() {
  const showChat = useStore(chatStore.showChat);
  const user = useStore(userStore.user);
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const description = useStore(descriptionStore);
  const [dropdownVisible, setDropdownVisible] = useState<DropdownVisibility>('none');

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        /**
         * Add a small delay before closing the dropdown to prevent interference
         * with dialogs that might be opening from menu items.
         */
        setTimeout(() => {
          setDropdownVisible('none');
        }, 100);
      } else if (dropdownVisible === 'none') {
        setDropdownVisible('menu');
      }
    },
    [dropdownVisible],
  );

  const handleTitleChange = useCallback((newTitle: string) => {
    setDropdownVisible('none');
    setDescription(newTitle);
  }, []);

  const handleRenameClick = useCallback(() => {
    setDropdownVisible('edit-title');
  }, []);

  const hasDescription = typeof description !== 'undefined';
  const currentProjectVisibility = useStore(projectStore.visibility);

  const currentProjectIcon = visibilityOptions.find(
    (option) => option.value === currentProjectVisibility,
  )?.iconClassName;

  const isLoading = !hasDescription;

  return (
    <>
      <header
        className={classNames(
          'flex shrink-0 select-none items-center pl-2 pr-3 h-[var(--header-height)] max-w-[var(--chat-min-width)]',

          /**
           * Necessary because the chat is fixed positioned so when chat is minimized it slides over the header and
           * the user can't click it.
           */
          !showChat && 'z-max',
        )}
      >
        <a href="/">
          <Button className="text-sm px-2" variant="ghost" size="md" truncate={false} title="Go home">
            <span className="i-bolt:logos-bolt-b size-6" />
          </Button>
        </a>

        <span className="text-bolt-elements-textPrimary opacity-[.12] text-xl antialiased mx-1">/</span>
        {user && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button className="text-sm px-2 -mr-px" variant="ghost" size="md" truncate={false}>
                <Avatar
                  size="xs"
                  user={{
                    name: activeOrganization ? activeOrganization.name : user?.name,
                    avatar: activeOrganization ? activeOrganization.avatar : user?.avatar,
                  }}
                />
                <span className="i-lucide:chevrons-up-down size-4 opacity-30 ml-1 -mr-1" />
              </Button>
            </DropdownMenu.Trigger>
            <LazyLoadWrapper name="AccountSelectorMenu">
              <AccountSelectorMenu user={user} activeOrganization={activeOrganization} side="bottom" align="start" />
            </LazyLoadWrapper>
          </DropdownMenu.Root>
        )}

        {user && <span className="text-bolt-elements-textPrimary opacity-[.12] text-xl antialiased mx-1">/</span>}

        <DropdownMenu.Root
          open={dropdownVisible === 'menu' || dropdownVisible === 'edit-title'}
          modal={false}
          onOpenChange={handleOpenChange}
        >
          <DropdownMenu.Trigger asChild>
            <Button
              className="text-sm px-2.5 transition-all duration-300 group shrink-auto"
              variant="ghost"
              size="md"
              truncate={false}
            >
              {isLoading ? (
                <Skeleton width="120px" height="12px" className="mt-px opacity-30" />
              ) : (
                <>
                  <span className="mt-px truncate max-w-80">{description}</span>
                  <span className={currentProjectIcon + ' shrink-0 ml-0.5'} />
                  <span
                    className={classNames(
                      'i-lucide:chevron-down size-4 opacity-0 transition-opacity duration-200 ml-1 -mr-1',
                      'group-hover:opacity-60',
                      (dropdownVisible === 'menu' || dropdownVisible === 'edit-title') && 'opacity-60',
                    )}
                  />
                </>
              )}
            </Button>
          </DropdownMenu.Trigger>
          <AnimatePresence>
            {dropdownVisible === 'edit-title' && (
              <ProjectRenameDropdownContent align="start" onTitleChange={handleTitleChange} />
            )}
            {dropdownVisible === 'menu' && (
              <DropdownMenu.Content
                align="start"
                asChild
                className="p-1 dark:shadow-lg border border-bolt-elements-borderColor rounded-md overflow-hidden select-none bg-bolt-elements-background-depth-2 min-w-[200px] z-visibility-dropdown text-sm"
                forceMount
                onCloseAutoFocus={(event) => event.preventDefault()}
                sideOffset={3}
              >
                <motion.div initial="close" animate="open" exit="close" variants={dropdownAnimationVariants}>
                  <ClientOnly>
                    {() => (
                      <LazyLoadWrapper name="ProjectHeaderDropDownItems">
                        <VersionHistoryDropdownItem isLoading={isLoading} />
                        <RenameDropdownItem isLoading={isLoading} onSelect={handleRenameClick} />
                        <DuplicateMenuItem isLoading={isLoading} />
                        <ExportDropdownItem isLoading={isLoading} />
                        <VisibilityDropdownItem isLoading={isLoading} />
                      </LazyLoadWrapper>
                    )}
                  </ClientOnly>
                </motion.div>
              </DropdownMenu.Content>
            )}
          </AnimatePresence>
        </DropdownMenu.Root>
      </header>

      {/* Version History Dialog - elevated outside dropdown structure */}
      <ClientOnly>
        {() => (
          <LazyLoadWrapper name="VersionHistoryDialog">
            <VersionHistoryRoot>
              {/* Empty trigger - dialog is controlled via store */}
              <div style={{ display: 'none' }} />
            </VersionHistoryRoot>
          </LazyLoadWrapper>
        )}
      </ClientOnly>
    </>
  );
}
